import { getChannelDescription, getChannelLabel } from "../../channels/metadata";
import { getUiLocale, tRuntime } from "../i18n/runtime";
import { ledgerTypes } from "./constants";
import type { BankTransaction, JournalEntry, ChannelId } from "./types";

export const channelLabel = (channel: ChannelId) =>
  tRuntime(`channel.label.${channel}`, getChannelLabel(channel));

export const channelDescription = (channel: ChannelId) =>
  tRuntime(`channel.description.${channel}`, getChannelDescription(channel));

export const formatStatusLabel = (status: string | null) => {
  if (!status) {
    return tRuntime("status.unknown", "Unknown");
  }
  const fallback = status
    .split("-")
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
  return tRuntime(`status.${status.toLowerCase()}`, fallback);
};

export const formatAmount = (amount: number | null, currency?: string | null) => {
  if (amount === null || Number.isNaN(amount)) {
    return "—";
  }
  const targetCurrency = currency ?? "DKK";
  try {
    const locale = getUiLocale() === "da" ? "da-DK" : "en-US";
    return new Intl.NumberFormat(locale, {
      style: "currency",
      currency: targetCurrency,
      maximumFractionDigits: 0,
    }).format(amount);
  } catch {
    return `${amount.toFixed(0)} ${targetCurrency}`;
  }
};

export const formatAmountValue = (amount: number | null) => {
  if (amount === null || Number.isNaN(amount)) {
    return "—";
  }
  try {
    const locale = getUiLocale() === "da" ? "da-DK" : "en-US";
    return new Intl.NumberFormat(locale, {
      maximumFractionDigits: 0,
    }).format(amount);
  } catch {
    return amount.toFixed(0);
  }
};

export const formatLedgerTypeLabel = (ledgerType: JournalEntry["ledgerType"]) =>
  (() => {
    const match = ledgerTypes.find((type) => type.id === ledgerType);
    if (!match) {
      return ledgerType;
    }
    return tRuntime(match.labelKey, match.label);
  })();

export const linkStatusStyle = (status: BankTransaction["linkStatus"]) => {
  if (status === "linked") {
    return "bg-emerald-100 text-emerald-700";
  }
  if (status === "partial") {
    return "bg-amber-100 text-amber-700";
  }
  return "bg-slate-100 text-slate-700";
};

export const formatMeterValue = (value: number) =>
  value.toLocaleString("da-DK", { maximumFractionDigits: 2 });

export const formatUsageValue = (value: number) =>
  value.toLocaleString("da-DK", { maximumFractionDigits: 1 });

export const formatDkkValue = (value: number) =>
  value.toLocaleString("da-DK", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export const formatSignedPercent = (value: number | null) =>
  value === null ? "—" : `${value >= 0 ? "+" : ""}${value.toFixed(1)}%`;

export const formatSignedDkk = (value: number | null) =>
  value === null ? "—" : `${value >= 0 ? "+" : ""}${value.toFixed(2)}`;

export const deviationPercentClass = (value: number | null) => {
  const tooLowPercent = Math.max(0, -(value ?? 0));
  if (tooLowPercent > 20) {
    return "text-red-700 font-medium";
  }
  if (tooLowPercent > 10) {
    return "text-amber-700 font-medium";
  }
  return "text-muted";
};

export const deviationDkkClass = (value: number | null) => {
  const tooLowDkk = Math.max(0, -(value ?? 0));
  if (tooLowDkk > 300) {
    return "text-red-700 font-medium";
  }
  if (tooLowDkk > 100) {
    return "text-amber-700 font-medium";
  }
  return "text-muted";
};
